import { ref, computed } from 'vue';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

// Bump this constant when rules actually change to trigger the rule change modal for existing users.
export const CURRENT_RULE_VERSION = '1.1';

const STORAGE_KEY_SEEN = 'lexicontest_tutorial_seen';
const STORAGE_KEY_RULE_VERSION = 'lexicontest_rule_version';

function readSeen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_SEEN) === 'true';
  } catch {
    return false;
  }
}

function readRuleVersion(): string {
  try {
    return localStorage.getItem(STORAGE_KEY_RULE_VERSION) ?? '';
  } catch {
    return '';
  }
}

// Module-level singleton state
const _hasSeen = ref(readSeen());
const _acknowledgedRuleVersion = ref(readRuleVersion());

const _codexOpen = ref(false);
const _briefingOpen = ref(false);
const _ruleChangeOpen = ref(false);

export function useTutorialState() {
  const hasSeen = _hasSeen as Readonly<typeof _hasSeen>;

  const hasAcknowledgedRules = computed(
    () => _acknowledgedRuleVersion.value === CURRENT_RULE_VERSION
  );

  function markSeen(): void {
    _hasSeen.value = true;
    try {
      localStorage.setItem(STORAGE_KEY_SEEN, 'true');
    } catch (e) {
      reportClientError(e, ErrorLocation.TutorialStatePersist);
    }
  }

  function markRulesAcknowledged(): void {
    _acknowledgedRuleVersion.value = CURRENT_RULE_VERSION;
    try {
      localStorage.setItem(STORAGE_KEY_RULE_VERSION, CURRENT_RULE_VERSION);
    } catch (e) {
      reportClientError(e, ErrorLocation.TutorialStatePersist);
    }
  }

  const codexOpen = _codexOpen as Readonly<typeof _codexOpen>;
  function openCodex(): void {
    _codexOpen.value = true;
  }
  function closeCodex(): void {
    _codexOpen.value = false;
  }

  const briefingOpen = _briefingOpen as Readonly<typeof _briefingOpen>;
  function openBriefing(): void {
    _briefingOpen.value = true;
  }
  function closeBriefing(): void {
    _briefingOpen.value = false;
  }

  const ruleChangeOpen = _ruleChangeOpen as Readonly<typeof _ruleChangeOpen>;
  function openRuleChange(): void {
    _ruleChangeOpen.value = true;
  }
  function closeRuleChange(): void {
    _ruleChangeOpen.value = false;
  }

  return {
    hasSeen,
    hasAcknowledgedRules,
    markSeen,
    markRulesAcknowledged,
    codexOpen,
    openCodex,
    closeCodex,
    briefingOpen,
    openBriefing,
    closeBriefing,
    ruleChangeOpen,
    openRuleChange,
    closeRuleChange,
  };
}
